import { useCallback } from 'react'
import { url } from '../../plugins/image'

/**
 * Hook for CSS generation and HTML manipulation utilities
 */
export const useStyleGeneration = () => {


  // Generate CSS for multiple classes
  const generateClassesCSS = useCallback((classesArray) => {
    if (!Array.isArray(classesArray)) return ''

    const uniqueClasses = new Map()

    // Ensure uniqueness by class name
    classesArray.forEach(cls => {
      if (cls?.name && cls?.properties && Object.keys(cls.properties).length > 0) {
        uniqueClasses.set(cls.name, cls)
      }
    })

    const generatedRules = Array.from(uniqueClasses.values())
      .map(classData => {
        if (!classData?.name || !classData?.properties) return ''

        const properties = Object.entries(classData.properties)
          .filter(([key, value]) => value && value.toString().trim() && key !== 'background-image-cdn')
          .map(([key, value]) => {
            let cssValue = value.toString().trim()

            // Handle background-image CDN resolution
            if (key === 'background-image' && classData.properties['background-image-cdn']) {
              const cdnPath = classData.properties['background-image-cdn']
              if (cdnPath && !cdnPath.startsWith('http')) {
                const fullUrl = url(cdnPath)
                cssValue = `url('${fullUrl}')`
              }
            }

            // Handle background property CDN resolution for images within gradients or complex backgrounds
            if (key === 'background' && classData.properties['background-image-cdn']) {
              const cdnPath = classData.properties['background-image-cdn']
              if (cdnPath && !cdnPath.startsWith('http') && cssValue.includes('url(')) {
                const fullUrl = url(cdnPath)
                cssValue = cssValue.replace(/url\(['"]*[^'"]*['"]*\)/g, `url('${fullUrl}')`)
              }
            }

            return `  ${key}: ${cssValue};`
          })
          .join('\n')

        if (!properties) return ''

        // Add CDN path as a CSS comment to preserve it
        let ruleWithComments = properties
        if (classData.properties['background-image-cdn']) {
          ruleWithComments = `  /* cdn: ${classData.properties['background-image-cdn']} */\n${properties}`
        }

        return `.${classData.name} {\n${ruleWithComments}\n}`
      })
      .filter(rule => rule)

    return generatedRules.join('\n\n')
  }, [])

  // Extract existing style content from HTML, separating managed classes from other CSS
  const extractStyleContent = useCallback((htmlContent, managedClassNames = []) => {
    if (!htmlContent) return { nonClassCSS: '', styleAttributes: '' }


    const styleTagRegex = /<style(\s[^>]*)?>([^]*?)<\/style>/i
    const match = htmlContent.match(styleTagRegex)

    if (!match) {
      return { nonClassCSS: '', styleAttributes: '' }
    }

    const styleAttributes = match[1] || ''
    const cssContent = match[2] || ''

    if (managedClassNames.length > 0) {
      // Remove managed class definitions from the original CSS
      let cleanedCSS = cssContent

      managedClassNames.forEach(className => {
        // Escape special characters including colons for modifiers
        const escapedClassName = className.replace(/[.*+?^${}()|[\]\\:]/g, '\\$&')

        // Find all occurrences of the class definition
        let searchIndex = 0
        while (true) {
          const classIndex = cleanedCSS.indexOf(`.${className}`, searchIndex)
          if (classIndex === -1) break

          // Find the opening brace
          const openBrace = cleanedCSS.indexOf('{', classIndex)
          if (openBrace === -1) break

          // Find the matching closing brace, handling nested braces
          let braceCount = 1
          let closeBrace = openBrace
          for (let i = openBrace + 1; i < cleanedCSS.length && braceCount > 0; i++) {
            if (cleanedCSS[i] === '{') braceCount++
            else if (cleanedCSS[i] === '}') braceCount--
            closeBrace = i
          }

          // Remove the entire class definition
          const beforeClass = cleanedCSS.substring(0, classIndex)
          const afterClass = cleanedCSS.substring(closeBrace + 1)
          cleanedCSS = beforeClass + afterClass

          // Continue searching from the current position
          searchIndex = classIndex
        }
      })

      return {
        nonClassCSS: cleanedCSS.trim(),
        styleAttributes
      }
    }

    return {
      nonClassCSS: cssContent.trim(),
      styleAttributes
    }
  }, [])

  // Generate updated HTML with new CSS classes (preserving original structure and only original classes)
  const generateUpdatedHTML = useCallback((originalHTML, classesArray, existingNonClassCSS = '') => {
    if (!originalHTML) return originalHTML

    // Filter to only include classes marked as original
    const originalClassesArray = classesArray.filter(cls => cls.isOriginal !== false)

    // Generate CSS for original classes only
    const newClassRules = generateClassesCSS(originalClassesArray)

    // Combine existing non-class CSS with new class rules
    const finalCSS = existingNonClassCSS + (existingNonClassCSS && newClassRules ? '\n\n' : '') + newClassRules

    // FIRST: Remove ONLY the managed original class definitions from ALL style tags (optimized)
    let cleanedHTML = originalHTML
    const managedClassNames = originalClassesArray.map(cls => cls.name)


    // Only process original classes that actually exist in the HTML for better performance
    managedClassNames.forEach(className => {
      if (originalHTML.includes(`.${className}`)) {
        // Escape colons and other special characters for modifiers
        const classRegex = new RegExp(`\\.${className.replace(/[.*+?^${}()|[\]\\:]/g, '\\$&')}\\s*\\{[\\s\\S]*?\\}`, 'g')
        cleanedHTML = cleanedHTML.replace(classRegex, '')
      }
    })


    // THEN: Replace or add style tag with new CSS
    const styleTagRegex = /<style(\s[^>]*)?>([^]*?)<\/style>/i
    const styleMatch = cleanedHTML.match(styleTagRegex)

    if (styleMatch) {
      // Replace existing style tag content
      const styleAttributes = styleMatch[1] || ''
      const newStyleTag = `<style${styleAttributes}>${finalCSS}</style>`
      return cleanedHTML.replace(styleMatch[0], newStyleTag)
    } else if (finalCSS) {
      // Add new style tag to head
      const headCloseIndex = cleanedHTML.indexOf('</head>')
      if (headCloseIndex !== -1) {
        const beforeHead = cleanedHTML.substring(0, headCloseIndex)
        const afterHead = cleanedHTML.substring(headCloseIndex)
        return `${beforeHead}<style>${finalCSS}</style>${afterHead}`
      }
    }

    return cleanedHTML
  }, [generateClassesCSS])

  // Parse CSS classes from HTML content (only from CSS definitions, not HTML class attributes)
  const parseClassesFromHTML = useCallback((htmlContent) => {
    if (!htmlContent || typeof htmlContent !== 'string') return []

    const classSet = new Set()
    const allMatches = [] // Track all matches for debugging

    // Extract classes ONLY from style tags (these are the actual class definitions)
    const styleTagRegex = /<style[^>]*>([\s\S]*?)<\/style>/gi
    let styleMatch
    while ((styleMatch = styleTagRegex.exec(htmlContent)) !== null) {
      const cssContent = styleMatch[1]
      if (cssContent) {
        // Regex to capture classes with modifiers (e.g., .bg-test:hover, .btn:nth-child(2))
        const cssClassRegex = /\.([a-zA-Z_][a-zA-Z0-9_-]*(?::[a-zA-Z-]+(?:\([^)]*\))?)?)/g
        let classMatch
        while ((classMatch = cssClassRegex.exec(cssContent)) !== null) {
          const className = classMatch[1]
          if (className) {
            allMatches.push(className)
            classSet.add(className)
          }
        }
      }
    }


    return Array.from(classSet)
  }, [])

  // Parse ONLY original CSS classes that have actual style definitions in the source
  const parseOriginalClassesFromHTML = useCallback((htmlContent) => {
    if (!htmlContent || typeof htmlContent !== 'string') return []

    const originalClasses = new Set()

    // Extract classes ONLY from style tags that exist in the original source
    const styleTagRegex = /<style[^>]*>([\s\S]*?)<\/style>/gi
    let styleMatch
    while ((styleMatch = styleTagRegex.exec(htmlContent)) !== null) {
      const cssContent = styleMatch[1]
      if (cssContent) {
        // Use a more precise regex to find complete class definitions
        const classDefinitionRegex = /\.([a-zA-Z_][a-zA-Z0-9_-]*(?::[a-zA-Z-]+(?:\([^)]*\))?)?)\s*\{([^}]*)\}/g
        let classMatch
        while ((classMatch = classDefinitionRegex.exec(cssContent)) !== null) {
          const className = classMatch[1]
          const classBody = classMatch[2].trim()

          // Only include classes that have actual CSS properties (not empty)
          if (className && classBody) {
            originalClasses.add(className)
          }
        }
      }
    }

    return Array.from(originalClasses)
  }, [])


  // Extract CSS properties for a specific class from HTML (supports modifiers)
  const extractClassProperties = useCallback((htmlContent, className) => {
    if (!htmlContent || !className) return {}

    const properties = {}
    // Escape special characters in class name, including colons for modifiers
    const escapedClassName = className.replace(/[.*+?^${}()|[\]\\:]/g, '\\$&')
    const styleRegex = new RegExp(`\\.${escapedClassName}\\s*\\{([^}]*)\\}`, 'g')

    let match
    while ((match = styleRegex.exec(htmlContent)) !== null) {
      const cssRules = match[1]
      if (cssRules) {
        // First, look for CDN path in CSS comments
        const cdnCommentMatch = cssRules.match(/\/\*\s*cdn:\s*([^*]+)\s*\*\//)
        if (cdnCommentMatch) {
          properties['background-image-cdn'] = cdnCommentMatch[1].trim()
        }

        // Then extract regular CSS properties
        const rules = cssRules.split(';')
        rules.forEach(rule => {
          const colonIndex = rule.indexOf(':')
          if (colonIndex > 0) {
            const property = rule.substring(0, colonIndex).trim()
            const value = rule.substring(colonIndex + 1).trim()
            if (property && value && !rule.trim().startsWith('/*')) {
              properties[property] = value
            }
          }
        })

        // If we have a CDN path but no background-image property, reconstruct it
        if (properties['background-image-cdn'] && !properties['background-image']) {
          const cdnPath = properties['background-image-cdn']
          if (cdnPath && !cdnPath.startsWith('http')) {
            // Convert CDN path to full URL using the imported url function
            const fullUrl = url(cdnPath)
            properties['background-image'] = `url('${fullUrl}')`
          }
        }
      }
    }

    return properties
  }, [])

  return {
    generateClassesCSS,
    extractStyleContent,
    generateUpdatedHTML,
    parseClassesFromHTML,
    parseOriginalClassesFromHTML,
    extractClassProperties
  }
}